import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import {
    AutocompleteElement,
    FormContainer,
    TextFieldElement,
} from 'react-hook-form-mui';
import { useNavigate } from 'react-router';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import { GridColDef, GridRowParams } from '@mui/x-data-grid';

import { DataGrid, DataGridCard } from '@barboxx/admin-components';
import {
    CREATE_PRODUCT_SCHEMA,
    ID_WIDTH,
    NAME_MIN_WIDTH,
} from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Product } from '@barboxx/models';
import { useBool } from '@barboxx/react-utils';
import { FLAVOR_TYPES } from '@barboxx/types';
import { enumToList } from '@barboxx/utils';

const columns: GridColDef<Product>[] = [
    {
        field: 'id',
        headerName: 'Id',
        width: ID_WIDTH,
    },
    {
        field: 'name',
        headerName: 'Name',
        minWidth: NAME_MIN_WIDTH,
        flex: 1,
    },
    {
        field: 'description',
        headerName: 'Description',
        minWidth: NAME_MIN_WIDTH,
        flex: 2,
    },
];

interface Props {
    product: Product.DetailsResponse;
    refetch: () => void;
}

export const ProductVariantsListCard = ({ product, refetch }: Props) => {
    const { id, flavorVariants, ...rest } = product;
    const navigate = useNavigate();
    const notifications = useNotifications();
    const [open, dialogActions] = useBool();

    const formContext = useForm<Product.CreateRequest>({
        resolver: yupResolver(CREATE_PRODUCT_SCHEMA) as any,
        defaultValues: {
            ...rest,
            name: '',
            description: '',
            baseProductId: id,
            flavor: null,
        },
    });

    const mutateCreateProduct = useMutation({
        mutationKey: ['productAttribute'],
        mutationFn: (data: Product.CreateRequest) =>
            Api.Products.Products.postProduct(data),
    });

    const onSubmit = async (values: Product.CreateRequest) => {
        return mutateCreateProduct
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${data?.name} product!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                refetch();
            })
            .catch(e => {
                notifications.show('Error creating product!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        dialogActions.off();
    };

    const handleRowClick = (params: GridRowParams) => {
        navigate(`/products/${params.id}`);
    };

    return (
        <>
            <DataGridCard
                title="Flavor Variants List"
                HeaderComponent={() => (
                    <Button variant="contained" onClick={dialogActions.on}>
                        Add Variant
                    </Button>
                )}
            >
                <DataGrid
                    columns={columns}
                    disableColumnMenu
                    disableRowSelectionOnClick
                    paginationMode="client"
                    onRowClick={handleRowClick}
                    rows={flavorVariants}
                />
            </DataGridCard>
            <Dialog
                open={open}
                onClose={dialogActions.off}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
            >
                <DialogTitle id="alert-dialog-title">
                    Create Product Variant
                </DialogTitle>
                <FormContainer onSuccess={onSubmit} formContext={formContext}>
                    <DialogContent
                        sx={{
                            gap: 2,
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <TextFieldElement label="Name" name="name" fullWidth />
                        <TextFieldElement
                            label="Description"
                            name="description"
                            fullWidth
                        />
                        <AutocompleteElement
                            label="Flavor"
                            name="flavor"
                            options={enumToList(FLAVOR_TYPES)}
                        />
                        {/* <TextFieldElement
                            label="Aging Requirements"
                            name="agingRequirements"
                            fullWidth
                        />
                        <TextFieldElement
                            label="Regional Requirements"
                            name="regionalRequirements"
                            fullWidth
                        />
                        <TextFieldElement
                            label="Other Specifications"
                            name="otherSpecifications"
                            fullWidth
                        /> */}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCancelCreate} color="inherit">
                            Cancel
                        </Button>

                        <Button
                            color="primary"
                            variant="contained"
                            loading={formContext.formState.isSubmitting}
                            type="submit"
                            disabled={formContext.formState.isSubmitting}
                        >
                            Create
                        </Button>
                    </DialogActions>
                </FormContainer>
            </Dialog>
        </>
    );
};
